import { IcosahedronGeometry, Mesh, MeshBasicMaterial } from "three/webgpu";
import Experience from "../Experience";
import { LANE_X, SEG_LEN } from "./TrackManager";

const POOL_SIZE = 10;
const SPAWN_FAR_Z = 130;
const SPAWN_NEAR_Z = 45;
const DESPAWN_BEHIND_Z = -10;
const BOMB_HEIGHT = 0.9;
const BOMB_RADIUS = 0.55;
const TRAIN_HIT_Z = 1.4;
const TRAIN_HIT_X = 1.0;
const SHOOT_HIT_RADIUS = 1.2;
const SPAWN_GAP_MIN = 14;
const SPAWN_GAP_MAX = 26;
// Local-Z within a segment runs [-SEG_LEN/2, +SEG_LEN/2]. The segment scrolls
// toward the train at z=0; the leading edge (smaller world-Z, local -SEG_LEN/2)
// reaches the train first, so a bomb spawned there hits the player right at the
// lane-commit moment. Allow bombs only on the trailing portion of each segment.
const BOMB_MIN_LOCAL_Z = 5;
const BOMB_MAX_LOCAL_Z = 12;
export const BOMB_SCORE = 2;

export default class Obstacles {
  constructor() {
    this.experience = new Experience();
    this.scene = this.experience.scene;

    this.geometry = new IcosahedronGeometry(BOMB_RADIUS, 1);
    this.material = new MeshBasicMaterial({ color: 0xee0000 });

    this.bombs = [];
    for (let i = 0; i < POOL_SIZE; i++) {
      const mesh = new Mesh(this.geometry, this.material);
      mesh.visible = false;
      this.scene.add(mesh);
      this.bombs.push({ mesh, alive: false, z: 0, lane: 1 });
    }
  }

  reset() {
    for (const b of this.bombs) {
      b.alive = false;
      b.mesh.visible = false;
    }
    const tm = this._tm();
    if (!tm) return;
    let z = SPAWN_NEAR_Z;
    for (const b of this.bombs) {
      if (z >= SPAWN_FAR_Z) break;
      this._spawnAt(b, z, tm);
      z += SPAWN_GAP_MIN + Math.random() * (SPAWN_GAP_MAX - SPAWN_GAP_MIN);
    }
  }

  update(delta, scrollSpeed) {
    const tm = this._tm();
    if (!tm) return false;
    const train = this.experience.world && this.experience.world.train;
    const trainX = train && train.group ? train.group.position.x : 0;

    const dz = scrollSpeed * delta;
    let trainHit = false;

    for (const b of this.bombs) {
      if (!b.alive) continue;
      b.z -= dz;
      b.mesh.position.z = b.z;
      b.mesh.rotation.y += delta * 1.6;

      const dx = LANE_X[b.lane] - trainX;
      if (Math.abs(b.z) < TRAIN_HIT_Z && Math.abs(dx) < TRAIN_HIT_X) {
        trainHit = true;
        const newZ = SPAWN_FAR_Z + Math.random() * 20;
        this._spawnAt(b, newZ, tm);
      } else if (b.z < DESPAWN_BEHIND_Z) {
        const newZ = SPAWN_FAR_Z + Math.random() * 20;
        this._spawnAt(b, newZ, tm);
      }
    }
    return trainHit;
  }

  tryHit(x, z, lane, radius = SHOOT_HIT_RADIUS) {
    const tm = this._tm();
    for (const b of this.bombs) {
      if (!b.alive) continue;
      if (lane !== undefined && b.lane !== lane) continue;
      const dx = LANE_X[b.lane] - x;
      const dz = b.z - z;
      if (dx * dx + dz * dz < radius * radius) {
        if (tm) {
          const newZ = SPAWN_FAR_Z + Math.random() * 20;
          this._spawnAt(b, newZ, tm);
        } else {
          b.alive = false;
          b.mesh.visible = false;
        }
        return true;
      }
    }
    return false;
  }

  _spawnAt(bomb, z, tm) {
    const lane = tm.getLaneAtZ(z);
    if (lane === null) {
      bomb.alive = false;
      bomb.mesh.visible = false;
      return;
    }
    // Constrain bomb to the trailing portion of the segment so the player has
    // room to either shoot it or sidestep after committing to the lane.
    const segZ = tm.getSegmentCenterAtZ(z);
    if (segZ !== null) {
      const local = z - segZ;
      if (local < BOMB_MIN_LOCAL_Z) z = segZ + BOMB_MIN_LOCAL_Z;
      else if (local > BOMB_MAX_LOCAL_Z) z = segZ + BOMB_MAX_LOCAL_Z;
    }
    bomb.lane = lane;
    bomb.z = z;
    bomb.alive = true;
    bomb.mesh.visible = true;
    bomb.mesh.position.set(LANE_X[lane], BOMB_HEIGHT, z);
  }

  _tm() {
    return this.experience.world && this.experience.world.trackManager;
  }
}
